import React from "react";
import { Link } from "react-router-dom";
import { Button } from "antd";
import { AlertTriangle, CheckCircle2, KeyRound, Loader, Server, ScrollText, ShieldOff, Terminal } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../../design-system/tokens";
import { card, PanelHead, CopyButton, InlineValue, Note } from "../sshProxyUi";
import { formatRelativeAge, orDash, proxyLiveness } from "../sshProxyModel";

// Step 3 — Verify.
//
// NOT a connect test. A fresh gateway has no policy, and SSH denies when
// nothing covers a session — so a test connection fails for everyone and the
// wizard would end on a failure every single time.
//
// Onboarding ends when the gateway is live. So this page shows what the console
// can actually verify from the heartbeat, then points at the things that
// make a connection possible — on the gateway detail page and the endpoints.
//
// `systemctl status` is not a substitute: the console cannot see its output, so
// it would be asking someone to run a command and self-report. The heartbeat
// proves the proxy is running, and proves it better.

function Fact({ label, children, mono = true }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: AN_MONO, fontSize: 9.5, letterSpacing: 0.9, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: mono ? AN_MONO : AN_FONT, fontSize: 12.5, color: AN_COLORS.ink, wordBreak: "break-all" }}>
        {children}
      </div>
    </div>
  );
}

function KeyLine({ label, value, pending }) {
  return (
    <div style={{ borderTop: `1px solid ${AN_COLORS.line2}`, paddingTop: 12, marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
        <span style={{ fontFamily: AN_MONO, fontSize: 11.5, fontWeight: 500, color: AN_COLORS.ink2 }}>{label}</span>
        {value ? <CopyButton text={value} label="Copy" /> : null}
      </div>
      {value ? (
        <div style={{ fontFamily: AN_MONO, fontSize: 12, background: AN_COLORS.tint, border: `1px solid ${AN_COLORS.line}`, borderRadius: 7, padding: "10px 12px", wordBreak: "break-all", color: AN_COLORS.ink2 }}>
          {value}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, border: `1px dashed ${AN_COLORS.line}`, borderRadius: 7, padding: "10px 12px", background: AN_COLORS.bg, color: AN_COLORS.muted, fontFamily: AN_FONT, fontSize: 12.5 }}>
          <Loader size={14} />
          {pending}
        </div>
      )}
    </div>
  );
}

function NextStep({ Icon, title, desc, to, cta }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, border: `1px solid ${AN_COLORS.line2}`, borderRadius: 8, padding: "12px 14px" }}>
      <span style={{ marginTop: 2, color: AN_COLORS.muted, flexShrink: 0 }}>
        <Icon size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: AN_COLORS.ink }}>{title}</div>
        <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 2, lineHeight: 1.5 }}>{desc}</div>
      </div>
      {to && (
        <Link to={to}>
          <Button size="small">{cta}</Button>
        </Link>
      )}
    </div>
  );
}

export default function Step3Verify({ state, jumpServerId }) {
  const live = proxyLiveness(state.lastSeenAt);
  const seen = live.state !== "never";

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ ...card, padding: 18 }}>
        <PanelHead
          eyebrow="Step 3 · Verify"
          title={seen ? "The gateway is live" : "Waiting for the gateway to check in"}
          desc={
            seen
              ? "Everything below is reported by the gateway itself, so it is the console's own evidence that the install worked."
              : "Onboarding finishes when the gateway reports in. Until it does, there is nothing here the console can vouch for."
          }
          right={seen ? <CheckCircle2 size={18} color={AN_COLORS.success} /> : <Loader size={18} color={AN_COLORS.muted} />}
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
          <Fact label="Liveness" mono={false}>{live.label}</Fact>
          <Fact label="Last heartbeat">
            {state.lastSeenAt ? formatRelativeAge((Date.now() - new Date(state.lastSeenAt).getTime()) / 1000) : "—"}
          </Fact>
          <Fact label="Version">{orDash(state.proxyVersion)}</Fact>
          <Fact label="Listening on">{orDash(state.proxyListen)}</Fact>
          <Fact label="Seen from">{orDash(state.lastSeenIp)}</Fact>
        </div>

        <KeyLine
          label="hostKeyFingerprint — what operators verify on first connect"
          value={state.hostKeyFingerprint}
          pending="Waiting for the gateway to report its host-key fingerprint."
        />
      </div>

      {/* What still has to happen before anyone can connect. All of it lives on
          the gateway page — none of it is onboarding. */}
      <div style={{ ...card, padding: 18 }}>
        <PanelHead
          eyebrow="Step 3 · Next"
          title="Before anyone can connect"
          desc="None of these are part of setup. They are settings on the gateway and its targets, changeable for their whole life."
        />

        <div style={{ display: "grid", gap: 8 }}>
          <NextStep
            Icon={KeyRound}
            title="Connect"
            desc="Each operator connects from Endpoints: open a browser terminal, or copy the ssh command for an 8-hour certificate from authnull login. Plain keys are refused."
            to="/pam/endpoint"
            cta="Endpoints"
          />
          <NextStep
            Icon={Server}
            title="Onboard your machines"
            desc="Enrol many at once with one command (by hand, Ansible, cloud-init or SSM), or add one at a time under Endpoints. Each machine's setup installs the account Authnull uses to issue and revoke keys."
            to={jumpServerId && jumpServerId !== "created" ? `/pam/jumpServer/enrol?gateway=${jumpServerId}` : "/pam/jumpServer/enrol"}
            cta="Enrol machines"
          />
          <NextStep
            Icon={ScrollText}
            title="Write a policy"
            desc="A policy must name the person, the host and the account. Each account it names gets its own key, issued automatically and released only for an approved session. A policy that leaves accounts empty grants nothing."
            to={jumpServerId && jumpServerId !== "created" ? `/pam/policy/v2?gateway=${jumpServerId}` : "/pam/policy/v2"}
            cta="Policies"
          />
          {/* The connection page is not built yet (it needs ssh/listConnections).
              Listing it without a link beats linking to a route that does not
              exist — /pam/connections is the DATABASE connections screen. */}
          <NextStep
            Icon={Terminal}
            title="Open the connection page"
            desc="Will list every target a policy grants you, with the exact connect command for each. Not available yet — the command must come from the server, because ssh splits on the last @ and a hand-assembled one fails with the same generic refusal as everything else."
          />
        </div>

        <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
          {/* Deny-by-default (#338) is live: with no policy, SSH refuses the session. */}
          <Note tone="warn" icon={<ShieldOff size={15} />}>
            <strong>No policy — sessions will be refused.</strong> SSH is deny-by-default, so nobody can connect through
            this gateway until a policy covers them.
          </Note>

          {/* The backend allowlist is no longer a bound operators maintain:
              empty means no local restriction and policy decides. */}
          <Note tone="info" icon={<AlertTriangle size={15} />}>
            No local bound is set on this gateway — targets are decided by policy. The gateway's{" "}
            <InlineValue>SSHPROXY_BACKEND_ALLOWLIST</InlineValue> is an optional extra restriction, not a prerequisite.
          </Note>
        </div>
      </div>
    </div>
  );
}
